import { HELP_ANCHORS, SCOPE_LEVEL_WORD, helpAnchor, type ScopeLevel } from "@keydials/ui";
import { DEFAULT_SEGMENT_NOUN, type SegmentNoun } from "../vocabulary";
import { Icon } from "../components/Icon";
import { StatusDot } from "../components/primitives";
import { sectionBandFill } from "@keydials/dial-renderer";

/**
 * KEY-513: which level of the dial is open — the whole dial, one section, or one action point.
 *
 * The three panels used to be near-identical cards (a heading, an underline tab strip starting with
 * "Overview"), so the only thing separating a section from an action point was a one-pixel heading size
 * and, at dial level, a pill tab strip. Nothing anywhere said the level out loud. This header sits above
 * whichever panel is mounted and says it three ways at once, so the answer survives losing any one of
 * them: a word, an icon, and a depth staircase.
 *
 * The level chip is deliberately colourless — inset surface, muted text — because everything else tinted
 * on this screen is a *status*, and a coloured chip beside a status-coloured dial would read as one.
 *
 * Rendered by DialWorkspace rather than by the panels: the panels are also mounted directly by tests and
 * by hosts that supply their own chrome (segmentScreenPass.test.tsx renders ActionPointPanel on its own),
 * and the level is a fact about the *navigation*, which is DialWorkspace's state, not the panel's.
 *
 * Inline styles, not a class: styles.css is a hand-maintained duplicate of web-user's theme.css for hosts
 * without the global tokens, so a new class would have to be added to both by hand and would drift. Every
 * token used here already exists in both.
 */

// Both now live in @keydials/ui (KEY-535) so the guided-help tour can build its prose from the same words
// the chip shows, instead of retyping them and drifting. Re-exported here because everything that renders a
// level already imports them from this file, and the level chip is still this file's subject.
export { SCOPE_LEVEL_WORD, type ScopeLevel };

/**
 * The three level words for ONE dial — the platform's, with the action-point level replaced by whatever this
 * definition calls its action points (KEY-539).
 *
 * A function of the noun rather than a second constant: the dial and section words are the platform's in
 * every dial, and only the third is configurable. {@link SCOPE_LEVEL_WORD} itself is left alone because the
 * guided-help catalogue builds its prose from it on the DASHBOARD, where no dial is open and so there is no
 * vocabulary to read.
 */
export function scopeLevelWord(noun: SegmentNoun): Record<ScopeLevel, string> {
  return { ...SCOPE_LEVEL_WORD, action: noun.One };
}

/** Depth, in px of indent — the staircase that makes "deeper" legible without reading a word. */
const INDENT: Record<ScopeLevel, number> = { dial: 0, section: 16, action: 32 };

export interface ScopeHeaderProps {
  level: ScopeLevel;
  /** The dial's name, the section's title, or the action point's name. */
  name: string;
  /** What sits one level up ("in Riverside depot" / "in Alpha"). Omitted at dial level. */
  parentName?: string | null;
  /** Steps up one level. Omitted at dial level, where there is nowhere to go. */
  onGoUp?: () => void;
  /** The selected action point's rollup colour — the rail and dot at action level. */
  colour?: string | null;
  /** The selected section's own band colour (KEY-755), when it has one — the rail at section level, so the
   * header ties back to the band that was clicked. Omitted = the structural navy every band defaults to. */
  sectionColour?: string | null;
  /** This dial's word for an action point (KEY-539). Omitted = the platform's. */
  noun?: SegmentNoun;
}

/**
 * The level word on its own — WHOLE DIAL / SECTION / ACTION POINT.
 *
 * Split out of {@link ScopeHeader} for KEY-532: in the Dynamic workspace the header's own card is gone and
 * the pill sits inside the panel's card instead, beside its title. Deliberately colourless in both places
 * (see the note above): everything else tinted on this screen is a status, and a chip that merely says what
 * KIND of thing you are looking at must not read as one.
 *
 * Keeps `data-scope-level`, which is how the workspace's tests find the level whichever way it is rendered.
 */
export function ScopePill({ level, noun = DEFAULT_SEGMENT_NOUN }: { level: ScopeLevel; noun?: SegmentNoun }) {
  return (
    <span
      data-scope-level={level}
      // The one part of the level display rendered in BOTH workspaces: Classic draws it inside the header
      // card below, Dynamic drops that card and puts this chip in the panel's own. A tour anchored on the
      // header would silently stop resolving for everyone on Dynamic, so the anchor lives here.
      {...helpAnchor(HELP_ANCHORS.scopeLevel)}
      style={{
        flex: "none",
        padding: "3px 7px",
        borderRadius: "var(--radius-sm)",
        background: "var(--surface-inset)",
        border: "var(--border-w) solid var(--border)",
        color: "var(--text-muted)",
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {scopeLevelWord(noun)[level]}
    </span>
  );
}

export function ScopeHeader({ level, name, parentName, onGoUp, colour, sectionColour, noun = DEFAULT_SEGMENT_NOUN }: ScopeHeaderProps) {
  // The rail ties back to the ring that was clicked: the accent for the dial as a whole, the section's band
  // (its own colour, KEY-755, else the structural navy), and the action point's own status colour.
  const band = sectionBandFill(sectionColour, "var(--ring-section)");
  const rail = level === "dial" ? "var(--accent)" : level === "section" ? band : colour || band;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        minWidth: 0,
        padding: "9px 13px",
        paddingLeft: 12 + INDENT[level],
        borderLeft: `3px solid ${rail}`,
        borderRadius: "var(--radius-sm)",
        background: "var(--surface-inset)",
      }}
    >
      <ScopePill level={level} noun={noun} />

      {/* The same vocabulary the search results already use for these two things (DialWorkspace's results
          list): a layers glyph for a section, the status dot for an action point. */}
      {level === "action" ? (
        <StatusDot colour={colour || band} size={9} />
      ) : (
        <Icon name={level === "dial" ? "target" : "layers"} size={15} style={{ flex: "none", color: "var(--text-faint)" }} />
      )}

      <span
        title={name}
        style={{
          minWidth: 0,
          color: "var(--text)",
          fontWeight: 700,
          fontSize: 15,
          fontFamily: "var(--font-display)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {name}
      </span>

      {/* An always-visible way back up one level, which the breadcrumb only offers while it is on screen. */}
      {parentName && onGoUp && (
        <button
          type="button"
          onClick={onGoUp}
          title={`Back to ${parentName}`}
          style={{
            font: "inherit",
            marginLeft: "auto",
            flex: "none",
            maxWidth: "40%",
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: "var(--text-faint)",
            fontSize: 12.5,
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          in {parentName}
        </button>
      )}
    </div>
  );
}
